import axios from 'axios'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import Button from '../../../button/Button'
import Input from '../../../input/Input'
import styles from './add-discount-code.module.scss'
import { baseApiURL } from 'src/utils/utils'
import { toast } from 'react-toastify'
import { StateContext } from 'src/AppContext'
import DropDown from 'src/components/dropDown/Dropdown'
import { IState } from 'src/Interface'
import AreaCodes from 'src/components/multiValueInput/AreaCodes'
import DatePickerComponent from 'src/components/datePicker/DatePickerComponent'
import { isSameDay, setHours, setMinutes, setSeconds } from 'date-fns'
import { DispatchContext } from 'src/AppContext'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaDiscountCampaignEventActions, gaDiscountCampaingMapper, gaUserMapper } from 'src/utils/ga/gaMapper'

interface Props {
  close: (closeAside?) => void
  discountCode: any
}


const AddDiscountCode: React.FunctionComponent<Props> = ({ close, discountCode }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState(new Date())
  const [expiryDate, setExpiryDate] = useState(null)
  const { cancellablePromise } = useCancellablePromise()
  const validCode = /^.{4,12}$/
  const [validationErrors, setValidationsErrors] = useState({
    name: false,
    zipCodesFrom: false,
    zipCodesTo: false
  })
  const [zipCodesFrom, setZipCodesFrom] = useState([])
  const [zipCodesTo, setZipCodesTo] = useState([])
  const [values, setValues] = useState({
    discount: '5',
    name: '',
    attributeCode: 'R5'
  })
  const dispatch = useContext(DispatchContext)
  const state: IState = useContext(StateContext)
  const [areaCodes, setAreaCodes] = useState([])

  useEffect(() => {
    if (discountCode) {
      setValues({
        discount: discountCode.percentage,
        name: discountCode.code,
        attributeCode: discountCode.attributeCode
      })
      setZipCodesFrom(discountCode.zipCodesFrom)
      setZipCodesTo(discountCode.zipCodesTo)
      setStartDate(new Date(discountCode.startDate))
      if (discountCode.expiryDate) {
        setExpiryDate(new Date(discountCode.expiryDate))
      }
    }
  }, [discountCode])

  useEffect(() => {
    if (state.user.accessToken) {
      cancellablePromise(axios(`${baseApiURL()}/dawa/zip_codes/grouped`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          setAreaCodes(result.data);
        }))
    }
  }, [cancellablePromise, discountCode, state.user.accessToken])

  const resetError = (fieldName: string) => {
    const validationErrorsCopy: any = { ...validationErrors }

    if (validationErrorsCopy) {
      validationErrorsCopy[fieldName] = false
    } else {
      validationErrorsCopy[fieldName] = false
    }
    setValidationsErrors(validationErrorsCopy)
  }

  const updateDiscountCode = () => {
    setIsLoading(true)
    axios.put(`${baseApiURL()}/discount_codes/${discountCode.id}`, {
      attributeCode: values.attributeCode,
      percentage: values.discount,
      code: values.name,
      startDate: isSameDay(new Date(startDate), new Date()) ? startDate.toISOString() : setHours(setMinutes(setSeconds(startDate, 0), 0), 0).toISOString().split('.')[0] + "Z",
      expiryDate: expiryDate ? setHours(expiryDate, 24).toISOString().split('.')[0] + "Z" : null,
      zipCodesFrom: zipCodesFrom,
      zipCodesTo: zipCodesTo
    }, {
      headers: {
        Authorization: `Bearer ${state.user.accessToken}`
      }
    }).then(() => {
      return null
    }).then(() => {
      return axios(`${baseApiURL()}/discount_codes`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
    }).then((result) => {
      dispatch({ type: 'SET_DISCOUNT_CODES', discountCodes: result.data })
      setIsLoading(false)
      close()
      toast('Rabatkode uppdaterad')
    }).catch(error => {
      if (error.response) {
        toast(error.response.data.message)
      } else {
        toast('Der gik desværre noget galt. Prøv igen om lidt')
      }
      setIsLoading(false)
    })
  }

  const createDiscountCode = () => {
    if (!values.name || !zipCodesFrom.length || !zipCodesTo.length) {
      setValidationsErrors({
        name: !values.name ? true : false,
        zipCodesFrom: !zipCodesFrom.length ? true : false,
        zipCodesTo: !zipCodesTo.length ? true : false
      })
    } else {
      setIsLoading(true)
      axios.post(`${baseApiURL()}/discount_codes`, {
        attributeCode: values.attributeCode,
        code: values.name,
        percentage: values.discount,
        startDate: isSameDay(new Date(startDate), new Date()) ? startDate.toISOString() : setHours(setMinutes(setSeconds(startDate, 0), 0), 0).toISOString().split('.')[0] + "Z",
        expiryDate: expiryDate ? setHours(expiryDate, 24).toISOString().split('.')[0] + "Z" : null,
        zipCodesFrom: zipCodesFrom.length === 1089 ? [] : zipCodesFrom,
        zipCodesTo: zipCodesTo.length === 1089 ? [] : zipCodesTo
      }, {
        headers: {
          Authorization: `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        return null
      }).then(() => {
        return axios(`${baseApiURL()}/discount_codes`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
      })
        .then((result) => {
          gaEvent(gaEventCategories.discounts, gaEvenActions.created_discount_campaign, gaUserMapper(state), gaDiscountCampaingMapper(gaDiscountCampaignEventActions.create, values, result.data[0]))
          dispatch({ type: 'SET_DISCOUNT_CODES', discountCodes: result.data })
          setIsLoading(false)
          close()
          toast('Rabatkode oprettet')
        }).catch(error => {
          setIsLoading(false)
          if (error.response) {
            toast(error.response.data.message)
          } else {
            toast('Der gik desværre noget galt. Prøv igen om lidt')
          }
        })
    }
  }

  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false
    if (value && regexp && !regexp.test(value || '')) {
      error = true
    }

    setValidationsErrors({
      ...validationErrors,
      [fieldName]: error
    })
  }



  const handleInputChange = (fieldName: string) => (value: string) => {
    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const handleDropDownChange = (attributeCode, discount) => {
    setValues({
      ...values,
      attributeCode: attributeCode,
      discount: discount
    })
  }

  const discounts = useMemo(() => {
    if (['R5', 'R8', '10', '11', '12', '15', '20', '25', '35', 'R5,FX'].includes(values.attributeCode)) {
      return [
        { label: '5% rabat', discount: '5', value: 'R5' },
        { label: '8% rabat', discount: '8', value: 'R8' },
        { label: '10% rabat', discount: '10', value: '10' },
        { label: '11% rabat', discount: '11', value: '11' },
        { label: '12% rabat', discount: '12', value: '12' },
        { label: '15% rabat', discount: '15', value: '15' },
        { label: '20% rabat', discount: '20', value: '20' },
        { label: '25% rabat', discount: '25', value: '25' },
        { label: '35% rabat', discount: '35', value: '35' },
        { label: '5% +F10', discount: '5', value: 'R5,FX' },
        { label: '10% + F10', discount: '10', value: '10,FX' },
      ]
    }
    return [
      { label: '5% rabat', discount: '5', value: 'R5' },
      { label: '8% rabat', discount: '8', value: 'R8' },
      { label: '10% rabat', discount: '10', value: '10' },
      { label: '11% rabat', discount: '11', value: '11' },
      { label: '12% rabat', discount: '12', value: '12' },
      { label: '15% rabat', discount: '15', value: '15' },
      { label: '20% rabat', discount: '20', value: '20' },
      { label: '25% rabat', discount: '25', value: '25' },
      { label: '35% rabat', discount: '35', value: '35' },
      { label: '5% +F10', discount: '5', value: 'R5,FX' },
      { label: '10% + F10', discount: '10', value: '10,FX' },
      { label: `${values.discount}%`, value: values.discount, attribute: values.attributeCode }
    ]
  }, [values.attributeCode, values.discount])

  const clearData = () => {
    setZipCodesFrom([])
    setStartDate(new Date())
    setExpiryDate(null)
    setZipCodesTo([])
    setValues({
      name: '',
      discount: '',
      attributeCode: ''
    })
  }




  return (
    <div className={styles.module}>
      <header className={styles.header}>
        <h4>{discountCode ? 'Redigér rabatkode' : 'Opret rabatkode'}</h4>
        <button className={styles.resetButton} onClick={clearData}>Nulstil</button>
      </header>
      <div className={styles.container}>
        <div className='tw-flex tw-mb-4'>
          <Input
            modifiers='tw-flex-3 tw-mr-4'
            label='Navn på rabatkode *'
            type='text'
            value={values.name}
            resetError={() => resetError('name')}
            error={validationErrors.name}
            handleValidation={() => handleValidation(validCode, 'name')}
            handleInputChange={() => handleInputChange('name')}
            name='name'
            placeholder='Vælg et navn'
          />
          <DropDown
            handleOnChange={handleDropDownChange}
            modifiers='tw-flex-1'
            placeholder=''
            sortOptions={false}
            options={discounts}
            label='Rabat *'
            value={values.attributeCode}
          />
        </div>
        <AreaCodes
          error={validationErrors.zipCodesFrom}
          selectedValues={zipCodesFrom}
          modifiers='tw-mb-4'
          resetError={() => resetError('zipCodesFrom')}
          list={areaCodes}
          label='Fra lokation *'
          areaCodeLabel='Fra lokation'
          name='zipCodesFrom'
          placeholder='Vælg by eller postnummer'
          updateList={setZipCodesFrom}
        />
        <AreaCodes
          error={validationErrors.zipCodesTo}
          resetError={() => resetError('zipCodesTo')}
          selectedValues={zipCodesTo}
          modifiers='tw-mb-4'
          list={areaCodes}
          areaCodeLabel='Til lokation'
          label='Til lokation *'
          name='zipCodesTo'
          placeholder='Vælg by eller postnummer'
          updateList={setZipCodesTo}
        />
        <div className='tw-flex tw-mb-4'>
          <DatePickerComponent
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            minDate={new Date()}
            maxDate={expiryDate ? expiryDate : undefined}
            modifiers='tw-mr-4 tw-flex-full'
            label='Start dato *'
          />
          <DatePickerComponent
            label='Slut dato'
            modifiers='tw-flex-full'
            selected={expiryDate}
            onChange={setExpiryDate}
            minDate={startDate}
          />
        </div>
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' handleClick={() => close()} />
        <Button disabled={validationErrors.name || validationErrors.zipCodesFrom || validationErrors.zipCodesTo} loading={isLoading} type='primary' label={discountCode ? 'Gem ændringer' : 'Opret rabatkode'} handleClick={discountCode ? updateDiscountCode : createDiscountCode} />
      </footer>
    </div>
  )
}

export default AddDiscountCode
